import React from 'react';
import { ShieldIcon, HomeIcon, HeartPulseIcon, BriefcaseIcon, PhoneIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface VerzekeringenPageProps {
  onNavigate: (page: string) => void;
}
export function VerzekeringenPage({
  onNavigate
}: VerzekeringenPageProps) {
  return <div className="w-full" data-id="element-731">
      {/* Hero */}
      <PageHero title="Zekerheid voor wat echt telt" subtitle="Geen onnodige polissen of kleine lettertjes. Ik zorg dat je goed verzekerd bent. Niet meer, niet minder. En als er iets gebeurt? Dan bel je mij." label="Verzekeringen" data-id="element-732">
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-733">
          <Button variant="secondary" size="lg" onClick={() => onNavigate('contact')} data-id="element-734">
            Verzekeringen doorspreken
          </Button>
          <Button variant="outline" size="lg" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-736" />} iconPosition="left" data-id="element-735">
            Schade melden
          </Button>
        </div>
      </PageHero>

      {/* Hoe werkt schade melden? */}
      <section className="py-20 md:py-28 bg-white" data-id="element-737">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-738">
          <SectionHeading title="Hoe werkt schade melden?" data-id="element-739" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-740">
            <FadeIn delay={0.1} className="text-center" data-id="element-741">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-742">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-743">
                Bel mij direct
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-744">
                Je krijgt direct je eigen adviseur aan de lijn. Geen keuzemenu's
                of wachttijden.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="text-center" data-id="element-745">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-746">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-747">Ik regel het</h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-748">
                Stuur me foto's via WhatsApp. Ik neem contact op met de
                verzekeraar en regel de afhandeling.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="text-center" data-id="element-749">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-6 shadow-md" data-id="element-750">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-751">
                Snel opgelost
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-752">
                Ik zorg voor een snelle uitkering of reparatie, zodat jij weer
                verder kunt.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Insurance Grid */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-753">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-754">
          <SectionHeading title="Verzekeringen" data-id="element-755" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-756">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-757">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-758">
                <HomeIcon className="w-7 h-7 text-gold" data-id="element-759" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-760">Wonen</h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-761">
                Opstal- en inboedelverzekering perfect afgestemd op jouw
                (nieuwe) woning of woonboot.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-762">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-763">
                <HeartPulseIcon className="w-7 h-7 text-gold" data-id="element-764" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-765">
                Overlijdensrisico
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-766">
                Financiële rust voor je nabestaanden. Zorg dat de hypotheek
                betaalbaar blijft als jij of je partner wegvalt.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-767">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-768">
                <BriefcaseIcon className="w-7 h-7 text-gold" data-id="element-769" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-770">
                Inkomensbescherming
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-771">
                Vang de financiële klap op bij arbeidsongeschiktheid of
                werkloosheid. Cruciaal voor ondernemers en huiseigenaren.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.6} className="mt-12 text-center" data-id="element-772">
            <p className="text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-773">
              Ik adviseer alleen over verzekeringen die direct relevant zijn
              voor jouw hypotheek en financiële situatie. Geen onnodige
              polissen, alleen wat je écht nodig hebt.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Trust Quote */}
      <section className="py-20 md:py-28 bg-white" data-id="element-774">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-775">
          <FadeIn className="bg-warm p-8 md:p-10 rounded-2xl border border-gray-100 shadow-card text-center" data-id="element-776">
            <div className="flex justify-center gap-1 mb-4" data-id="element-777">
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-778" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-779" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-780" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-781" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-782" />
            </div>
            <p className="text-gray-700 italic text-lg leading-relaxed mb-4" data-id="element-783">
              "Na een waterschade was alles binnen een week geregeld. Eén
              telefoontje en Jordy nam het volledig over. Dat is pas service."
            </p>
            <p className="font-bold text-navy" data-id="element-784">Familie Bakker</p>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Op zoek naar hypotheekadvies?" subtitle="De verzekeringen sluiten naadloos aan op jouw hypotheek. Bekijk ook het hypotheekadvies." buttonText="Bekijk hypotheekadvies" buttonVariant="secondary" onNavigate={onNavigate} page="hypotheken" variant="light" data-id="element-785" />
    </div>;
}